class StoreLocationsComponent extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slidesPerView = 1;
    this.onResize = this.handleResize.bind(this);
    this.onPreviousClick = this.previous.bind(this);
    this.onNextClick = this.next.bind(this);
    this.onCountryChange = this.handleCountryChange.bind(this);
  }

  connectedCallback() {
    this.behavior = this.dataset.behavior || 'carousel';
    this.countrySelect = this.querySelector('[data-country-select]');

    if (this.behavior === 'carousel') {
      this.track = this.querySelector('[data-locations-track]');
      this.slides = Array.from(this.querySelectorAll('[data-location-slide]')).filter(
        (slide) => slide instanceof HTMLElement
      );
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.emptyState = this.querySelector('[data-empty-state]');
      this.slidesDesktop = Number.parseInt(this.dataset.slidesDesktop || '4', 10);
      this.slidesTablet = Number.parseInt(this.dataset.slidesTablet || '2', 10);
      this.gridGap = Number.parseFloat(this.dataset.gridGap || '0');

      this.countrySelect?.addEventListener('change', this.onCountryChange);
      this.previousButton?.addEventListener('click', this.onPreviousClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      window.addEventListener('resize', this.onResize);

      this.filterSlides();
      this.updateSlidesPerView();
      this.updateCarousel();
      return;
    }

    if (this.behavior === 'page') {
      this.countrySelect?.addEventListener('change', this.onCountryChange);
      this.accordions = Array.from(this.querySelectorAll('[data-locations-accordion]')).filter(
        (accordion) => accordion instanceof HTMLDetailsElement
      );
    }
  }

  disconnectedCallback() {
    this.countrySelect?.removeEventListener('change', this.onCountryChange);
    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    window.removeEventListener('resize', this.onResize);
  }

  handleResize() {
    this.updateSlidesPerView();
    this.currentIndex = this.isMobileGridMode()
      ? 0
      : Math.min(this.currentIndex, this.getTotalPages() - 1);
    this.updateCarousel();
  }

  handleCountryChange() {
    if (!(this.countrySelect instanceof HTMLSelectElement)) {
      return;
    }

    if (this.behavior === 'page') {
      const targets = Array.from(this.querySelectorAll(`[data-country-target="${this.countrySelect.value}"]`)).filter(
        (target) => target instanceof HTMLElement
      );
      const visibleTarget = targets.find((target) => target.offsetParent !== null) || targets[0];

      if (Array.isArray(this.accordions) && visibleTarget instanceof HTMLDetailsElement) {
        for (const accordion of this.accordions) {
          accordion.open = accordion === visibleTarget;
        }
      }

      if (visibleTarget instanceof HTMLElement) {
        visibleTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      return;
    }

    this.currentIndex = 0;
    this.filterSlides();
    this.updateSlidesPerView();
    this.updateCarousel();
  }

  updateSlidesPerView() {
    const viewportWidth = window.innerWidth;

    if (viewportWidth <= 749) {
      this.slidesPerView = 1;
    } else if (viewportWidth <= 989) {
      this.slidesPerView = this.slidesTablet;
    } else {
      this.slidesPerView = this.slidesDesktop;
    }
  }

  isMobileGridMode() {
    return this.dataset.mobileLayout === 'grid' && window.innerWidth <= 749;
  }

  getVisibleSlides() {
    if (!Array.isArray(this.slides)) {
      return [];
    }

    return this.slides.filter((slide) => !slide.hidden);
  }

  getTotalPages() {
    const visibleSlides = this.getVisibleSlides();
    return Math.max(1, Math.ceil(visibleSlides.length / this.slidesPerView));
  }

  filterSlides() {
    if (!Array.isArray(this.slides)) {
      return;
    }

    const selectedCountry = this.countrySelect instanceof HTMLSelectElement ? this.countrySelect.value : '';
    let visibleSlides = 0;

    for (const slide of this.slides) {
      const countryHandle = slide.dataset.countryHandle || '';
      const shouldShow = selectedCountry === '' || selectedCountry === countryHandle;
      slide.hidden = !shouldShow;

      if (shouldShow) {
        visibleSlides += 1;
      }
    }

    if (this.emptyState instanceof HTMLElement) {
      this.emptyState.hidden = visibleSlides > 0;
    }
  }

  updateCarousel() {
    if (!(this.track instanceof HTMLElement)) {
      return;
    }

    if (this.isMobileGridMode()) {
      this.track.style.transform = 'translateX(0px)';

      if (this.previousButton instanceof HTMLButtonElement) {
        this.previousButton.disabled = true;
      }

      if (this.nextButton instanceof HTMLButtonElement) {
        this.nextButton.disabled = true;
      }

      return;
    }

    const visibleSlides = this.getVisibleSlides();
    const totalPages = this.getTotalPages();
    this.currentIndex = Math.min(this.currentIndex, totalPages - 1);

    if (!visibleSlides.length) {
      this.track.style.transform = 'translateX(0px)';

      if (this.previousButton instanceof HTMLButtonElement) {
        this.previousButton.disabled = true;
      }

      if (this.nextButton instanceof HTMLButtonElement) {
        this.nextButton.disabled = true;
      }

      return;
    }

    const firstVisibleSlide = visibleSlides[0];

    if (!(firstVisibleSlide instanceof HTMLElement)) {
      return;
    }

    const slideWidth = firstVisibleSlide.offsetWidth;
    const offset = -(this.currentIndex * this.slidesPerView * (slideWidth + this.gridGap));
    this.track.style.transform = `translateX(${offset}px)`;

    if (this.previousButton instanceof HTMLButtonElement) {
      this.previousButton.disabled = this.currentIndex === 0;
    }

    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = this.currentIndex >= totalPages - 1;
    }
  }

  previous(event) {
    event.preventDefault();

    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.updateCarousel();
    }
  }

  next(event) {
    event.preventDefault();

    if (this.currentIndex < this.getTotalPages() - 1) {
      this.currentIndex += 1;
      this.updateCarousel();
    }
  }
}

if (!customElements.get('store-locations-component')) {
  customElements.define('store-locations-component', StoreLocationsComponent);
}
